<script lang="ts" setup>
import {
  EvaluationCriteria,
  EvaluationCriteriaConfiguration,
  EvaluationRelevantInstance,
} from "@/types/EvaluationResults";
import { storeToRefs } from "pinia";
import type { ComputedRef } from 'vue';
import { CriteriaInstance } from "types/Checklist";
import { useDocumentHighlightStore } from "~/composables/useDocumentHighlightStore";

const { criteriaInstance, criteriaConfig, index, relevantInstance } =
  defineProps({
    criteriaInstance: {
      type: Object as PropType<CriteriaInstance>,
      required: true,
    },
    criteriaConfig: {
      type: Object as PropType<EvaluationCriteriaConfiguration>,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    relevantInstance: {
      type: Object as PropType<EvaluationRelevantInstance>,
      required: true,
    },
    paragraphPosition: {
      type: Number,
      required: false,
    },
    isPlaceholder: {
      type: Boolean,
      default: false,
    },
    hasMultipleInstances: {
      type: Boolean,
      required: true,
    },
  });

const emits = defineEmits(["reportProblem", "highlightAll"]);

const documentId = inject("documentId") as any;
const inheritedReviewInstance = inject<ComputedRef<EvaluationRelevantInstance | null> | null>('metricReviewRootInstance', null);
provide('metricReviewRootInstance', computed(() => inheritedReviewInstance?.value ?? relevantInstance));

const shouldExpandByDefault = (): boolean => {
  if (criteriaConfig.expandByDefault == true) {
    return true;
  }

  if (relevantInstance.criteria && relevantInstance.criteria.length > 0) {
    var hasManualCriteria = false;
    var hasFailedCriteria = false;

    for (var criteria of relevantInstance.criteria) {
      if (criteria.passed != true) {
        hasFailedCriteria = true;
      }

      if (criteria.id == "manual") {
        hasManualCriteria = true;
      }
    }

    if (hasManualCriteria || hasFailedCriteria) {
      return true;
    }
  }

  return false;
};

const isExpanded = ref(shouldExpandByDefault());

const toggleIsExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const { clearSelectedHighlight, setTokenPositionsToHighlight } =
  useDocumentHighlightStore(documentId);

const onMouseEnter = () => {
  if (criteriaConfig.highlightInstances == true) {
    if (!relevantInstance.tokens) {
      return;
    }

    var positions = relevantInstance.tokens.map((token) =>
      tokenGlobalPositionFromId.value(token.id)
    );
    positions = positions.filter((position) => position != null);

    setTokenPositionsToHighlight(positions as number[]);
  }
};

const onMouseLeave = () => {
  if (criteriaConfig.highlightInstances == true) {
    clearSelectedHighlight();
  }
};

const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { getCriteriaFromId } = documentChecklistStore;

const criteria = computed(() => {
  return criteriaInstance.checklistCriteriaId
    ? getCriteriaFromId(criteriaInstance.checklistCriteriaId)
    : undefined;
});

const showParagarphPoints = computed(() => {
  return criteriaConfig.acceptParagraphPoints == true;
});

// OK so now I need to create a copy o this instance and assign this criteria to it
const onReportProblem = (criteria: EvaluationCriteria, isControl: boolean) => {
  const instanceCopy = { ...relevantInstance };
  emits("reportProblem", instanceCopy, isControl);
};

const reportProblem = (
  instance: EvaluationRelevantInstance,
  isControl: boolean
) => {
  const instanceCopy = JSON.parse(JSON.stringify(instance));
  emits("reportProblem", instanceCopy, isControl);
};

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const shouldShow = computed(() => {
  if (isTeacherViewMode.value == true) return true;

  return criteriaConfig.shouldShowToStudents;
});
</script>

<template>
  <div
    :data-step-id="relevantInstance.location"
    class="flex flex-col relevant-instance-list-item pt-1"
    :class="{
      '': criteriaConfig.isTopLevelCriteria || true,
      '': criteriaConfig.isTopLevelCriteria || true,
      '': true,
    }"
    v-if="criteria"
  >
    <BaseDelayedMouseover
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      v-if="criteriaConfig.shouldShowInstances == true"
    >
      <div
        class="flex flex-row justify-between items-start py-[4px] pl-2 min-h-[40px] rounded"
        :class="{
          'pl-0': criteriaConfig.isTopLevelCriteria && false,
          'hover:bg-opacity-10 hover:bg-medium-default cursor-pointer':
            !criteriaConfig.isTopLevelCriteria || true,
        }"
        :data-step-id="`relevant-instance-list-item-${relevantInstance.location}`"
      >
        <EvaluationResultsInstancesListLeftContent
          class="flex flex-row grow mr-2"
          :criteria="criteria"
          :criteria-instance="criteriaInstance"
          :criteria-config="criteriaConfig"
          :relevant-instance="relevantInstance"
          :index="index"
          :is-expanded="isExpanded || criteriaConfig.forceOpen == true"
          @click="toggleIsExpanded"
          @highlight-all="($event) => $emit('highlightAll', $event)"
        />
        <EvaluationResultsInstancesListRightContent
          :criteria-instance="criteriaInstance"
          :criteria-config="criteriaConfig"
          :relevant-instance="relevantInstance"
          :index="index"
          :has-multiple-instances="hasMultipleInstances"
          @report-problem="reportProblem"
        />
      </div>
    </BaseDelayedMouseover>

    <div v-if="isPlaceholder">
      <span class="italic text-body-1 pl-4"> Not Found </span>
    </div>
    <div v-if="shouldShow && relevantInstance?.criteria?.length > 0">
      <TransitionsCollapsible>
        <EvaluationResultsCriteriaList
          v-show="isExpanded || criteriaConfig.forceOpen == true"
          :class="{
            'pl-0': criteriaConfig.isTopLevelCriteria != true,
          }"
          :criteria-instance="criteriaInstance"
          :criteria-list="relevantInstance.criteria"
          :checklist-criteria="criteria"
          :relevant-instance="relevantInstance"
          :paragraph-position="showParagarphPoints ? index : paragraphPosition"
          @reportProblem="onReportProblem"
        ></EvaluationResultsCriteriaList>
      </TransitionsCollapsible>
    </div>
  </div>
</template>

<style scoped></style>
